<!--
  CollectorDoneSheet — «خلصت {المحل}؟» (collector-app P1-D, handoff spec Section
  2.3; mockup Done.dc.html).

  The collector closes a shop here. The sheet NEVER blocks: lines still without
  a mark are resolved inside it (block A, only when there are any), the paid
  figure is what the backend computed from his marks, and the one required
  field is the paper's total — EMPTY by default, so he has to read it off the
  paper — unless the shop gave no paper («المحل مادّاش فاتورة»). The difference
  line is live; there is no confirm dialog (the shop page stays editable until
  the leader reviews).

    block A   «علّمهم مش موجود» → the page queues one `out_of_stock` per line;
              «المحل مقفول — سيبهم لليدر» → the page queues ONE `shop_left`.
              No third option. Both hand the decision to the page (every tap
              goes through the action layer); the list empties by itself
              because the day re-renders with the taps applied.
    invoice   FILED TO THE PHONE (collector-offline P1-B; collector/invoiceQueue.js):
              «سجّل وخلّص المحل» writes the filing and the sheet closes at once,
              with or without signal, like every tap. The queue sends it by
              itself after his marks. The sheet never hears the answer: a
              filing the leader overruled or the server refused is a toast and
              ONE card on the day page (CollectorConflictCards).
              While a filing of this shop waits, the sheet opens PREFILLED with
              it (the amount, or the no-paper choice, and its paper as taken)
              under one line saying so; filing again replaces it.
              The PAPER'S PHOTO is shrunk on the phone and kept in the `photos`
              store the moment it is taken (one per shop, collector-app P2-C),
              brought back when this shop's sheet is opened again — a closed
              sheet or an app kill never costs him the picture. It is not
              uploaded by the photo queue: it rides the filing request.

  «خلّصت» is the backend's word: after filing, the page re-reads the day and
  the shop's state arrives from the API — this sheet never sets it.

  UNDER the paid figure sits the list of the lines behind it (collector-clarity
  P3-A, spec Section 6.7): one row per collected line with its own sum, a line
  bought at another quantity or price in amber — that is where a difference
  with the paper usually hides. The total is still `shop.spentCents`; the list
  explains it and never replaces it. It scrolls with the sheet.

  Fresh state on every open. A definite height, the action in the fixed footer.
-->
<template>
  <f7-sheet
    class="collector-surface c-done-sheet"
    :opened="opened"
    backdrop
    close-by-backdrop-click
    @sheet:closed="onClosed"
  >
    <f7-page-content v-if="shop">
      <div class="c-done">
        <div class="c-done__head">
          <h2 class="c-done__title">
            {{ t('collector.doneSheet.title', { shop: shopName }) }}
          </h2>
          <button
            type="button"
            class="c-reset c-done__close"
            :aria-label="t('collector.doneSheet.close')"
            @click="emit('update:opened', false)"
          >
            <svg width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              aria-hidden="true"
            ><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <!-- ── block A: lines still without a mark ── -->
        <section v-if="pending.length" class="c-unmarked">
          <div class="c-unmarked__title">
            {{ t('collector.doneSheet.unmarkedTitle', { items: unmarkedItems }) }}
          </div>
          <div v-for="line in pending" :key="line.id" class="c-unmarked__line c-num">
            {{ unmarkedLine(line) }}
          </div>
          <div class="c-unmarked__actions">
            <button type="button" class="c-reset c-outline" @click="emit('mark-oos', pending)">
              {{ t('collector.doneSheet.markOos') }}
            </button>
            <button type="button" class="c-reset c-outline" @click="emit('leave-shop', shop)">
              {{ t('collector.doneSheet.leaveShop') }}
            </button>
          </div>
        </section>

        <!-- ── what I paid, by my marks ── -->
        <section class="c-paid">
          <div class="c-paid__row">
            <div class="c-paid__label">{{ t(shop.paidByWallet ? 'collector.doneSheet.walletShopTotal' : 'collector.doneSheet.paidByMarks') }}</div>
            <div class="c-paid__amount c-num">
              {{ pounds(shop.spentCents) }} <span>{{ t('collector.cash.unit') }}</span>
            </div>
          </div>
          <div class="c-paid__caption">{{ collectedCaption }}</div>

          <!-- the lines behind the total: where a difference with the paper hides -->
          <div v-if="paid.length" class="c-paidlines c-num">
            <div
              v-for="row in paid"
              :key="row.line.id"
              class="c-paidline"
              :class="{ 'c-paidline--chg': row.changed }"
            >
              <i18n-t
                keypath="collector.doneSheet.paidLine"
                tag="span"
                scope="global"
                class="c-paidline__name"
              >
                <template #product>
                  <b>{{ row.line.productName }}</b>
                </template>
                <template #code>{{ row.line.memberCode ?? '—' }}</template>
              </i18n-t>
              <span class="c-paidline__calc">{{ paidCalc(row) }}</span>
              <span class="c-paidline__sum">{{ signed(row.paidCents) }}</span>
            </div>
          </div>

          <template v-if="canInvoice">
            <div v-if="waiting" class="c-queued">{{ t('collector.doneSheet.queuedNote') }}</div>
            <label class="c-paid__label" for="c-invoice">{{ t('collector.doneSheet.invoiceLabel') }}</label>
            <div class="c-money">
              <input
                id="c-invoice"
                v-model="invoiceText"
                class="c-field c-num"
                type="text"
                inputmode="decimal"
                :disabled="noPaper"
                :class="{ 'c-field--bad': invoiceBad }"
                @focus="onFocus"
              />
              <span class="c-money__unit">{{ t('collector.cash.unit') }}</span>
            </div>

            <div
              v-if="difference"
              class="c-diff c-num"
              :class="difference.matched ? 'c-diff--ok' : 'c-diff--off'"
            >
              {{ differenceText }}
            </div>

            <div class="c-paper">
              <label class="c-paper__shoot" :class="{ 'c-paper__shoot--taken': photoState === 'ready' }">
                <input
                  class="c-paper__input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  @change="onPhoto"
                />
                <svg width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                ><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></svg>
                <span>{{ photoLabel }}</span>
              </label>
              <button type="button" class="c-reset c-paper__none" @click="noPaper = !noPaper">
                {{ noPaper ? t('collector.doneSheet.noPaperOn') : t('collector.doneSheet.noPaper') }}
              </button>
            </div>
          </template>
          <div v-else class="c-paid__caption">{{ t('collector.doneSheet.noShop') }}</div>
        </section>

        <section v-if="!shop?.paidByWallet" class="c-cash">
          <div class="c-paid__label">{{ t('collector.doneSheet.cashAfter') }}</div>
          <div class="c-paid__amount c-num">
            {{ pounds(cashAfter) }} <span>{{ t('collector.cash.unit') }}</span>
          </div>
        </section>

        <div class="c-done__footer">{{ t('collector.doneSheet.footer') }}</div>
      </div>
    </f7-page-content>

    <template #fixed>
      <div v-if="shop && canInvoice" class="sheet-fixed-footer">
        <button
          type="button"
          class="c-reset c-submit"
          :disabled="!canSubmit"
          @click="submit"
        >
          <span>{{ submitting ? t('collector.doneSheet.submitting') : t('collector.doneSheet.submit') }}</span>
          <svg width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
        </button>
      </div>
    </template>
  </f7-sheet>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { countBucket, countPhrase, phraseText, signedPounds } from '@/collector/dayView.js';
import { useCollectorInvoicesStore } from '@/collector/invoiceQueue.js';
import {
  cashAfterShop,
  centsToInputText,
  invoiceDifference,
  isRejected,
  parsePoundsToCents,
  unmarkedLines,
} from '@/collector/lineView.js';
import { paidLines } from '@/collector/moneyView.js';
import { useCollectorPhotosStore } from '@/collector/photoQueue.js';
import { prepareCollectorPhoto } from '@/collector/photos.js';
import { useCurrency } from '@/composables/useCurrency.js';

const props = defineProps({
  opened: { type: Boolean, default: false },
  /** The shop, live from the day document (null while none is chosen). */
  shop: { type: Object, default: null },
  planId: { type: [String, Number], default: null },
  /** `cash.withMeCents` right now (advisory — counts this shop at my marks). */
  cashWithMeCents: { type: Number, default: 0 },
});
/** `mark-oos` carries the pending lines, `leave-shop` the shop; `filed` (the filing is on the phone) closes the sheet. */
const emit = defineEmits(['update:opened', 'mark-oos', 'leave-shop', 'filed']);

const { t } = useI18n();
const photos = useCollectorPhotosStore();
const invoices = useCollectorInvoicesStore();
const { formatAmount } = useCurrency();
const pounds = (cents) => formatAmount(cents, { trimZeros: true });

const shopName = computed(() => props.shop?.name ?? t('collector.shops.noShop'));
/** The lines the sheet asks about — empty once the shop is closed by the collector. */
const pending = computed(() => unmarkedLines(props.shop));
const unmarkedItems = computed(() => phraseText(countPhrase('items', pending.value.length), t, pounds));
const canInvoice = computed(() => props.shop?.supplierId != null);

/** «3 أصناف اتجابوا · العربون اللي دفعته العميلة في المحل مش محسوب عليك» */
const collectedCaption = computed(() => {
  const n = Number(props.shop?.counters?.collected) || 0;
  const bucket = n === 0 ? 'zero' : countBucket(n);
  return t(`collector.count.collectedCaption.${bucket}`, { n });
});

/** The collected lines, in the shop's line order, each with the sum his mark stands for. */
const paid = computed(() => paidLines(props.shop));

/** A deposit larger than the line is not floored, so the amount can go below zero (U+2212, on its digits). */
const signed = (cents) => pounds(cents).replace('-', '−');

/** «2 × 320», «2 من 3 × 160», and «− عربون 200» after it when she paid a deposit there. */
function paidCalc(row) {
  const qty = row.foundQty === row.qty
    ? String(row.foundQty)
    : t('collector.figures.foundOf', { found: row.foundQty, qty: row.qty });
  const calc = `${qty} × ${row.priceCents > 0 ? pounds(row.priceCents) : '—'}`;
  return row.depositCents > 0
    ? `${calc} ${t('collector.doneSheet.depositPart', { deposit: pounds(row.depositCents) })}`
    : calc;
}

function unmarkedLine(line) {
  const base = t('collector.doneSheet.unmarkedLine', {
    product: line.productName,
    code: line.memberCode ?? '—',
    qty: phraseText(countPhrase('pieces', line.qty ?? 0), t, pounds),
  });
  return isRejected(line) ? `${base} ${t('collector.doneSheet.unmarkedRejected')}` : base;
}

// ── The invoice ───────────────────────────────────────────────────────────────

const invoiceText = ref('');
const noPaper = ref(false);
/** 'none' | 'preparing' | 'ready' | 'failed' */
const photoState = ref('none');
let photoBlob = null;
const submitting = ref(false);

const paper = () => ({ planId: props.planId, supplierId: props.shop?.supplierId });

/** This shop's filing still waiting on the phone, live from the queue (null once it is sent). */
const waiting = computed(() => {
  const { planId, supplierId } = paper();
  if (planId == null || supplierId == null) return null;
  return (
    invoices.queued.find(
      (filing) => String(filing.planId) === String(planId) && String(filing.supplierId) === String(supplierId),
    ) ?? null
  );
});

watch(
  // The plan too: the same supplier on his other plan of today is another invoice —
  // what was typed against one plan's marks must never be filed under the other.
  // Separate sources, compared one by one: a getter returning an array fired on
  // every rebuild of the day (each offline retry) and wiped the typed amount.
  [() => props.opened, () => props.shop?.supplierId, () => props.planId],
  async ([opened, supplierId, planId]) => {
    if (!opened) return;
    invoiceText.value = '';
    noPaper.value = false;
    photoState.value = 'none';
    photoBlob = null;
    submitting.value = false;
    if (supplierId == null || props.planId == null) return;
    const still = () => props.opened && props.shop?.supplierId === supplierId && props.planId === planId;
    // A filing of this shop already waiting on the phone: the sheet opens with
    // what he filed. Never over something he typed or chose meanwhile.
    await invoices.ensureLoaded();
    const filed = waiting.value;
    if (filed && still() && invoiceText.value === '' && !noPaper.value) {
      if (filed.noPaper) noPaper.value = true;
      else if (filed.amountCents != null) invoiceText.value = centsToInputText(filed.amountCents);
    }
    // The paper's photo kept from an earlier visit (a closed sheet, an app
    // kill, a filing still waiting) comes back; one taken meanwhile is not replaced.
    const held = await photos.heldInvoicePhoto(paper());
    if (held && still() && photoState.value === 'none') {
      photoBlob = held;
      photoState.value = 'ready';
    }
  },
  { immediate: true },
);

const invoiceCents = computed(() => {
  if (noPaper.value || invoiceText.value.trim() === '') return null;
  return parsePoundsToCents(invoiceText.value);
});
const invoiceBad = computed(() => !noPaper.value && invoiceText.value.trim() !== '' && invoiceCents.value == null);

const difference = computed(() => invoiceDifference(invoiceCents.value, props.shop?.spentCents));

/** «الفرق −60 ج.م · قارن السطور اللي فوق بالفاتورة قبل ما تمشي» / «مظبوط ✓» */
const differenceText = computed(() =>
  difference.value?.matched
    ? t('collector.doneSheet.matched')
    : t('collector.doneSheet.difference', { diff: signedPounds(difference.value?.differenceCents ?? 0) }),
);

const cashAfter = computed(() => cashAfterShop(props.cashWithMeCents, invoiceCents.value, props.shop?.spentCents));

const photoLabel = computed(() => {
  if (photoState.value === 'preparing') return t('collector.doneSheet.photoPreparing');
  if (photoState.value === 'ready') return t('collector.doneSheet.photoTaken');
  return t('collector.doneSheet.photo');
});

async function onPhoto(event) {
  const input = event.target;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;
  photoState.value = 'preparing';
  try {
    photoBlob = await prepareCollectorPhoto(file);
    photoState.value = 'ready';
    // Kept on the phone at once (one per shop, a new one replaces it); never awaited.
    if (props.shop?.supplierId != null && props.planId != null) photos.holdInvoicePhoto({ ...paper(), blob: photoBlob });
  } catch {
    photoBlob = null;
    photoState.value = 'failed';
    toast(t('collector.doneSheet.photoFailed'));
  }
}

const canSubmit = computed(
  () =>
    canInvoice.value &&
    !submitting.value &&
    photoState.value !== 'preparing' &&
    (noPaper.value || invoiceCents.value != null),
);

function toast(text) {
  try {
    f7?.toast?.create({ text, position: 'center', closeTimeout: 3500 }).open();
  } catch {
    /* no F7 app (tests, early boot) */
  }
}

/**
 * «سجّل وخلّص المحل»: the filing is WRITTEN TO THE PHONE and the sheet closes —
 * it never waits for the network. `submitting` covers the write only. The
 * queue sends it after his marks; its answer is not this sheet's to hear.
 */
async function submit() {
  if (!canSubmit.value) return;
  submitting.value = true;
  try {
    const filing = await invoices.file({
      planId: props.planId,
      supplierId: props.shop.supplierId,
      amountCents: noPaper.value ? null : invoiceCents.value,
      noPaper: noPaper.value,
      blob: photoBlob,
    });
    emit('filed', filing);
  } catch (error) {
    // Only a caller's bug gets here (no plan, no shop): nothing was kept, so say so.
    console.error('[CollectorDoneSheet] the invoice was not filed', error);
    toast(t('collector.doneSheet.failed'));
  } finally {
    submitting.value = false;
  }
}

/** A field under the keyboard scrolls into view once the keyboard is up. */
function onFocus(event) {
  const el = event.target;
  setTimeout(() => el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 300);
}

function onClosed() {
  photoBlob = null;
  if (props.opened) emit('update:opened', false);
}
</script>

<style scoped>
.c-done-sheet {
  height: 92%;
  background: var(--c-ground);
  --sheet-fixed-footer-h: 80px;
  --f7-page-content-extra-padding-bottom: var(--sheet-fixed-footer-h);
}

.c-done {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 16px 8px;
}

.c-done__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.c-done__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}

.c-done__close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: var(--c-radius-chip);
  background: var(--c-card);
  color: var(--c-ink);
}

.c-done__footer {
  padding: 0 8px;
  font-size: 12px;
  color: var(--c-muted);
  text-align: center;
}

.c-unmarked {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border: 1px solid var(--c-amber-border);
  border-radius: var(--c-radius-row);
  background: var(--c-amber-fill);
}

.c-unmarked__title { font-size: 15px; font-weight: 700; color: var(--c-amber-text); }
.c-unmarked__line { font-size: 13px; color: var(--c-amber-ink); }

.c-unmarked__actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.c-outline {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  min-height: 48px;
  padding: 6px 8px;
  border: 2px solid var(--c-amber-pill);
  border-radius: var(--c-radius-chip);
  background: var(--c-card);
  color: var(--c-amber-text);
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  box-sizing: border-box;
}

.c-paid,
.c-cash {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: var(--c-radius-row);
  background: var(--c-card);
}

.c-paid__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.c-paid__label { font-size: 15px; font-weight: 700; }

.c-paid__amount {
  font-size: 22px;
  font-weight: 700;
}

.c-paid__amount span {
  font-size: 13px;
  font-weight: 500;
  color: var(--c-muted);
}

.c-paid__caption {
  font-size: 12px;
  color: var(--c-muted);
  line-height: 1.5;
}

.c-paidlines {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--c-border);
  border-radius: var(--c-radius-chip);
}

.c-paidline {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--c-border);
  font-size: 13px;
}

.c-paidline:last-child { border-bottom: 0; }

.c-paidline--chg { background: var(--c-amber-fill); }

.c-paidline__name {
  flex: 1 1 0;
  min-width: 0;
  color: var(--c-ink-soft);
}

.c-paidline__name b { color: var(--c-ink); }

.c-paidline__calc {
  white-space: nowrap;
  color: var(--c-ink-soft);
}

.c-paidline--chg .c-paidline__calc {
  font-weight: 700;
  color: var(--c-amber-text);
}

.c-paidline__sum {
  min-width: 44px;
  white-space: nowrap;
  text-align: end;
  font-size: 15px;
  font-weight: 700;
}

.c-money {
  display: flex;
  align-items: center;
  gap: 8px;
}

.c-money__unit {
  flex-shrink: 0;
  font-size: 13px;
  color: var(--c-muted);
}

.c-field {
  flex: 1 1 auto;
  min-width: 0;
  height: 56px;
  padding: 0 14px;
  border: 2px solid var(--c-border);
  border-radius: var(--c-radius-chip);
  background: var(--c-ground);
  color: var(--c-ink);
  font: inherit;
  font-size: 22px;
  font-weight: 700;
  text-align: center;
  box-sizing: border-box;
}

.c-queued {
  padding: 8px 12px;
  border-radius: var(--c-radius-chip);
  background: var(--c-lilac);
  color: var(--c-indigo);
  font-size: 13px;
  line-height: 1.5;
}

.c-field:disabled { opacity: 0.45; }
.c-field--bad { border-color: var(--c-red-border); }

.c-diff {
  padding: 8px 12px;
  border-radius: var(--c-radius-chip);
  font-size: 13px;
  font-weight: 700;
}

.c-diff--ok { background: var(--c-green-fill); color: var(--c-green-text); }
.c-diff--off { background: var(--c-amber-fill); color: var(--c-amber-text); }

.c-paper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.c-paper__shoot {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 14px;
  border: 2px solid var(--c-purple);
  border-radius: var(--c-radius-chip);
  color: var(--c-indigo);
  font-size: 14px;
  font-weight: 700;
  box-sizing: border-box;
  cursor: pointer;
}

.c-paper__shoot--taken {
  border-color: var(--c-green-text);
  color: var(--c-green-text);
}

.c-paper__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.c-paper__none {
  min-height: 44px;
  padding: 0 6px;
  color: var(--c-indigo);
  font-size: 14px;
  font-weight: 700;
  text-decoration: underline;
  text-align: center;
}

.c-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 56px;
  border-radius: var(--c-radius-row);
  background: var(--c-indigo);
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
}

.c-submit:disabled {
  background: var(--c-dot);
  color: var(--c-ink-soft);
}
</style>
